import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import "../styles/pages/ProductDetailsPage.css";
import ProductGallery from "../components/product/ProductGallery";
import ProductCard from "../components/product/ProductCard";
import QuantitySelector from "../components/product/QuantitySelector";
import LoadingState from "../components/shared/LoadingState";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { useProducts } from "../contexts/ProductContext";
import { useWishlist } from "../contexts/WishlistContext";
import { Product } from "../types/store";
import { formatCurrency } from "../utils/currency";
import { MobileIcon, MobilePageHeader } from "../components/layout/MobileAppChrome";
import { checkPincodeServiceability } from "../services/accountService";
import { readSelectedAddress, SELECTED_ADDRESS_UPDATED_EVENT } from "../utils/selectedAddress";
import { toast } from "../utils/toast";
import useBodyScrollLock from "../hooks/useBodyScrollLock";

const ProductDetailsPage: React.FC = () => {
  const { slug = "" } = useParams();
  const navigate = useNavigate();
  const { addToCart, itemCount: cartCount } = useCart();
  const { user, isAuthenticated } = useAuth();
  const { isInWishlist, itemCount: wishlistCount, toggleWishlist } = useWishlist();
  const { getProductBySlug, getRelatedProducts, loading } = useProducts();
  const [product, setProduct] = useState<Product | null>(null);
  const [resolved, setResolved] = useState(false);
  const [checkingPincode, setCheckingPincode] = useState(false);
  const [selectedAddressPincode, setSelectedAddressPincode] = useState("");
  const [selectedAddressServiceability, setSelectedAddressServiceability] = useState<{
    loading: boolean;
    serviceable: boolean | null;
    message: string | null;
  }>({
    loading: false,
    serviceable: null,
    message: null,
  });
  const [deliveryPincode, setDeliveryPincode] = useState("");
  const [deliveryPincodeMessage, setDeliveryPincodeMessage] = useState<string | null>(null);
  const [quantityModalOpen, setQuantityModalOpen] = useState(false);
  const [quantityModalAction, setQuantityModalAction] = useState<"cart" | "buy" | null>(null);
  const [quantityDraft, setQuantityDraft] = useState(1);
  const [quantityModalNotice, setQuantityModalNotice] = useState<string | null>(null);
  const [addedToCartThisVisit, setAddedToCartThisVisit] = useState(false);

  useBodyScrollLock(quantityModalOpen);

  React.useEffect(() => {
    setAddedToCartThisVisit(false);
    const loadProduct = async () => {
      const resolvedProduct = await getProductBySlug(slug);
      setProduct(resolvedProduct || null);
      setResolved(true);
    };
    void loadProduct();
  }, [getProductBySlug, slug]);

  useEffect(() => {
    const syncSelectedAddress = () => {
      const storedAddress = readSelectedAddress(user);
      setSelectedAddressPincode(storedAddress?.postalCode?.trim() || "");
    };

    syncSelectedAddress();
    window.addEventListener(SELECTED_ADDRESS_UPDATED_EVENT, syncSelectedAddress);
    return () => window.removeEventListener(SELECTED_ADDRESS_UPDATED_EVENT, syncSelectedAddress);
  }, [user]);

  useEffect(() => {
    if (!selectedAddressPincode) {
      setSelectedAddressServiceability({
        loading: false,
        serviceable: null,
        message: null,
      });
      return;
    }

    const normalized = selectedAddressPincode.trim();
    if (!/^\d{6}$/.test(normalized)) {
      setSelectedAddressServiceability({
        loading: false,
        serviceable: null,
        message: "Selected address does not have a valid 6-digit pincode.",
      });
      return;
    }

    let cancelled = false;
    setSelectedAddressServiceability({
      loading: true,
      serviceable: null,
      message: null,
    });

    const resolveServiceability = async () => {
      try {
        const response = await checkPincodeServiceability(normalized);
        if (!cancelled) {
          setSelectedAddressServiceability({
            loading: false,
            serviceable: response.serviceable,
            message: response.message,
          });
        }
      } catch {
        if (!cancelled) {
          setSelectedAddressServiceability({
            loading: false,
            serviceable: null,
            message: "Unable to check serviceability right now.",
          });
        }
      }
    };

    void resolveServiceability();
    return () => {
      cancelled = true;
    };
  }, [selectedAddressPincode]);

  if (loading || !resolved) {
    return (
      <section className="shell section page-section details-page">
        <LoadingState cardCount={2} />
      </section>
    );
  }

  if (!product) {
    return (
      <section className="shell section page-section details-page">
        <div className="store-card empty-state">
          <h2>Product not found</h2>
          <p>The item you were looking for is no longer available in the catalog.</p>
          <Link className="button" to="/products">
            Return to products
          </Link>
        </div>
      </section>
    );
  }

  const relatedProducts = getRelatedProducts(product);
  const addedToCart = addedToCartThisVisit;
  const savedToWishlist = isInWishlist(product.id);
  const discountLabel = `${product.discountPercentage}% OFF`;
  const descriptionParagraphs = product.description
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);
  const hiddenTagSlugs = new Set([
    "hard-to-find-products",
    "everyday-essentials",
    "electrical-essentials",
    "hardware-tools",
    "plumbing-bathroom",
    "summer",
    "monsoon",
  ]);
  const featureItems = Array.from(
    new Set(
      [product.heroTag, ...product.tags]
        .map((tag) => tag.trim())
        .filter((tag) => tag && !hiddenTagSlugs.has(tag.toLowerCase())),
    ),
  );
  const specs = [
    { label: "Brand", value: product.brand },
    { label: "Category", value: product.category },
    { label: "Subcategory", value: product.subcategory || "All products" },
    { label: "Warranty", value: product.warrantyAvailable ? "Available" : "Not listed" },
    { label: "Replacement", value: product.replacementAvailable ? "Available" : "Not listed" },
  ];
  const handleShare = async () => {
    const shareUrl = window.location.href;
    const sharePayload = {
      title: product.name,
      text: `Check out ${product.name} on Eldoo`,
      url: shareUrl,
    };

    if (navigator.share) {
      await navigator.share(sharePayload);
      return;
    }

    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`${sharePayload.text} ${sharePayload.url}`)}`;
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  };

  const handleToggleWishlist = async () => {
    if (savedToWishlist) {
      navigate("/wishlist");
      return;
    }
    await toggleWishlist(product);
  };

  const openQuantityModal = (action: "cart" | "buy") => {
    if (product.availability === "out-of-stock") {
      return;
    }

    setQuantityModalAction(action);
    setQuantityDraft(1);
    setQuantityModalNotice(null);
    setQuantityModalOpen(true);
  };

  const closeQuantityModal = () => {
    setQuantityModalOpen(false);
    setQuantityModalAction(null);
    setQuantityModalNotice(null);
  };

  const handleQuantityLimitReached = (limit: number) => {
    const message = `Stock available: ${limit} item${limit === 1 ? "" : "s"}.`;
    setQuantityModalNotice(message);
    toast.error(message);
  };

  const handleConfirmQuantity = async () => {
    if (!product || !quantityModalAction) {
      return;
    }

    const selectedQuantity = Math.max(0, Math.min(quantityDraft, Math.min(10, product.stockQuantity)));
    if (selectedQuantity <= 0) {
      const message = "Select at least 1 item.";
      setQuantityModalNotice(message);
      toast.error(message);
      return;
    }

    closeQuantityModal();

    if (quantityModalAction === "buy" && !isAuthenticated) {
      window.location.assign(`/login?redirect=${encodeURIComponent("/cart")}`);
      return;
    }

    const added = await addToCart(product, selectedQuantity);
    if (added && quantityModalAction === "cart") {
      setAddedToCartThisVisit(true);
    }

    if (quantityModalAction === "buy") {
      navigate("/cart");
    }
  };

  const handlePincodeCheck = async () => {
    const normalized = deliveryPincode.trim();
    if (!/^\d{6}$/.test(normalized)) {
      toast.error("Enter a valid 6-digit pincode.");
      return;
    }

    setCheckingPincode(true);
    try {
      const response = await checkPincodeServiceability(normalized);
      setDeliveryPincodeMessage(response.message);
      if (response.serviceable) {
        toast.success(response.message);
      } else {
        toast.error(response.message);
      }
    } catch {
      setDeliveryPincodeMessage("Unable to check serviceability right now.");
      toast.error("Unable to check serviceability right now.");
    } finally {
      setCheckingPincode(false);
    }
  };
  const modalMaxQuantity = Math.max(1, Math.min(10, product.stockQuantity));
  return (
    <section className="shell section page-section details-page">
      <MobilePageHeader
        title="Product Details"
          actions={(
            <>
              <Link
                to="/wishlist"
                className="details-page__header-action"
                aria-label={`Wishlist${wishlistCount > 0 ? `, ${wishlistCount} items` : ""}`}
              >
                <MobileIcon name="heart" />
                {wishlistCount > 0 ? <span className="details-page__header-badge">{wishlistCount}</span> : null}
              </Link>
              <Link
                to="/cart"
                className="details-page__header-action"
              aria-label={`Cart${cartCount > 0 ? `, ${cartCount} items` : ""}`}
            >
              <MobileIcon name="cart" />
              {cartCount > 0 ? <span className="details-page__header-badge">{cartCount}</span> : null}
            </Link>
          </>
        )}
      />
      <div className="details-layout">
        <div className="details-gallery-wrap">
          <span className="details-badge">{discountLabel}</span>
          <div className="details-gallery-actions">
            <button type="button" className="details-gallery-action" aria-label="Share product" onClick={() => void handleShare()}>
              <MobileIcon name="share" />
            </button>
            <button
              type="button"
              className={`details-gallery-action details-gallery-action--wishlist ${savedToWishlist ? "is-active" : ""}`}
              aria-label={savedToWishlist ? "View wishlist" : "Add to wishlist"}
              onClick={() => void handleToggleWishlist()}
            >
              <MobileIcon name="heart" />
            </button>
          </div>
          <ProductGallery images={product.images} alt={product.name} />
        </div>

        <div className="store-card details-panel">
            <div className="details-panel__scroll">
              <div className="details-brand">
                <strong>{product.brand}</strong>
              </div>
            <h1>{product.name}</h1>
            <div className={`details-stock-line ${product.availability === "out-of-stock" ? "is-out-of-stock" : "is-stock"}`}>
              {product.availability === "out-of-stock"
                ? "Out of stock"
                : "In stock"}
            </div>

            <div className="details-price-stack">
              <div className="details-price">
                <strong>{formatCurrency(product.price)}</strong>
                <span>{formatCurrency(product.originalPrice)}</span>
              </div>
              <div className="details-offer-line">{discountLabel}</div>
            </div>

            {false && <div className="details-delivery-box">
              <div className="details-delivery-box__copy">
                <strong>Deliver to</strong>
                {selectedAddressPincode ? (
                  <span className="details-delivery-box__pincode">{selectedAddressPincode}</span>
                ) : (
                  <div className="details-delivery-box__inline-check">
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={deliveryPincode}
                      onChange={(event) => {
                        const digitsOnly = event.target.value.replace(/\D/g, "").slice(0, 6);
                        setDeliveryPincode(digitsOnly);
                        setDeliveryPincodeMessage(null);
                      }}
                      placeholder="Enter pincode"
                      aria-label="Enter delivery pincode"
                    />
                    <button type="button" onClick={() => void handlePincodeCheck()} disabled={checkingPincode}>
                      {checkingPincode ? "Checking..." : "Check"}
                    </button>
                  </div>
                )}
                {selectedAddressPincode ? (
                  <>
                    <span
                      className={`details-delivery-box__availability ${
                        selectedAddressServiceability.loading
                          ? "is-checking"
                          : selectedAddressServiceability.serviceable === false
                            ? "is-unserviceable"
                            : "is-serviceable"
                      }`}
                    >
                      {selectedAddressServiceability.loading
                        ? "Checking serviceability..."
                        : selectedAddressServiceability.serviceable === false
                          ? "Not serviceable"
                          : selectedAddressServiceability.serviceable === true
                            ? "Delivery in 10-48 Hours"
                            : "Checking serviceability..."}
                    </span>
                    {selectedAddressServiceability.message ? (
                      <small className="details-delivery-box__message">
                        {selectedAddressServiceability.message}
                      </small>
                    ) : null}
                  </>
                ) : deliveryPincodeMessage ? (
                  <small className="details-delivery-box__message">{deliveryPincodeMessage}</small>
                ) : null}
              </div>
            </div>}

            <div className="details-hero-tag">
              <span>{product.heroTag}</span>
            </div>

            <div className="details-purchase">
              {product.availability === "out-of-stock" ? (
                <button type="button" className="details-action-button details-action-button--notify">
                  <span aria-hidden="true">🔔</span>
                  <span>Notify me</span>
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="details-action-button details-action-button--primary"
                    onClick={() => (addedToCart ? navigate("/cart") : openQuantityModal("cart"))}
                  >
                    <span aria-hidden="true">🛒</span>
                    <span>{addedToCart ? "View cart" : "Add to cart"}</span>
                  </button>
                  <button
                    type="button"
                    className="details-action-button details-action-button--secondary"
                    onClick={() => openQuantityModal("buy")}
                  >
                    <span aria-hidden="true">⚡</span>
                    <span>Buy Now</span>
                  </button>
                </>
              )}
            </div>

            <div className="details-info-stack">
              {descriptionParagraphs.length ? (
                <section className="details-info-block">
                  <h2>Description</h2>
                  <div className="details-info-copy">
                    {descriptionParagraphs.map((paragraph, index) => (
                      <p key={`${index}-${paragraph.slice(0, 24)}`}>{paragraph}</p>
                    ))}
                  </div>
                </section>
              ) : null}

              {featureItems.length ? (
                <section className="details-info-block">
                  <h2>Features</h2>
                  <div className="details-tag-list">
                    {featureItems.map((feature) => (
                      <span key={feature}>{feature}</span>
                    ))}
                  </div>
                </section>
              ) : null}

              <section className="details-info-block">
                <h2>Full Specs</h2>
                <dl className="details-spec-grid">
                  {specs.map((spec) => (
                    <div key={spec.label} className="details-spec-item">
                      <dt>{spec.label}</dt>
                      <dd>{spec.value}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            </div>

            <div className="details-trust-grid" aria-label="Secure purchase highlights">
              <div className="details-trust-item">
                <span className="details-trust-item__icon" aria-hidden="true">
                  ✓
                </span>
                <div>
                  <strong>Secure payments</strong>
                  <span>Encrypted checkout</span>
                </div>
              </div>
              <div className="details-trust-item">
                <span className="details-trust-item__icon" aria-hidden="true">
                  ⟲
                </span>
                <div>
                  <strong>Warranty support</strong>
                  <span>{product.warrantyAvailable ? "Available" : "Not listed"}</span>
                </div>
              </div>
              <div className="details-trust-item">
                <span className="details-trust-item__icon" aria-hidden="true">
                  ❤
                </span>
                <div>
                  <strong>Genuine products</strong>
                  <span>Verified inventory</span>
                </div>
              </div>
              <div className="details-trust-item">
                <span className="details-trust-item__icon" aria-hidden="true">
                  ☎
                </span>
                <div>
                  <strong>Support available</strong>
                  <span>Call or chat anytime</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {relatedProducts.length > 0 ? (
        <section className="details-related-products" aria-label="Related products">
          <div className="section-heading details-related-products__heading">
            <div>
              <span className="eyebrow">Suggestions</span>
              <h2>Related products</h2>
              <p>More in {product.category}</p>
            </div>
          </div>
          <div className="details-related-products__rail">
            {relatedProducts.map((relatedProduct) => (
              <ProductCard key={relatedProduct.id} product={relatedProduct} compact />
            ))}
          </div>
        </section>
      ) : null}

      {quantityModalOpen && quantityModalAction ? (
        <div
          className="details-quantity-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Select quantity"
        >
          <div className="details-quantity-modal__dialog" onClick={(event) => event.stopPropagation()}>
            <div className="details-quantity-modal__header">
              <div>
                <h2>Select quantity</h2>
                <p>{product.name}</p>
              </div>
              <button type="button" className="details-quantity-modal__close" onClick={closeQuantityModal} aria-label="Close">
                ×
              </button>
            </div>

            <div className="details-quantity-modal__body">
              <QuantitySelector
                value={quantityDraft}
                min={0}
                max={modalMaxQuantity}
                onChange={(value) => {
                  setQuantityDraft(value);
                  setQuantityModalNotice(null);
                }}
                onMaxReached={handleQuantityLimitReached}
              />
              <small className="details-quantity-modal__note">
                {quantityModalNotice || `Maximum ${modalMaxQuantity} item${modalMaxQuantity === 1 ? "" : "s"} can be selected.`}
              </small>
            </div>

            <div className="details-quantity-modal__actions">
              <button type="button" className="details-quantity-modal__button details-quantity-modal__button--ghost" onClick={closeQuantityModal}>
                Cancel
              </button>
              <button
                type="button"
                className="details-quantity-modal__button details-quantity-modal__button--primary"
                onClick={() => void handleConfirmQuantity()}
              >
                {quantityModalAction === "buy" ? "Continue to checkout" : "Add to cart"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default ProductDetailsPage;
